import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import {
  Ambulance,
  ArrowLeft,
  HeartPulse,
  History,
  KeyRound,
  LogOut,
  Printer,
  RefreshCw,
  Save,
  Search,
  UsersRound
} from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { supabaseClient } from '../lib/supabaseClient';
import { compareResidentsByKana, hasResidentKana } from '../lib/residentSort';

type ResidentStatus = 'active' | 'retired' | 'inactive';

type ResidentMaster = {
  id: string;
  display_order: number | null;
  name: string;
  kana: string | null;
  room: string | null;
  active: boolean;
  status: ResidentStatus;
  care_level: string | null;
  gender: string | null;
  birth_date: string | null;
  age_at_import: number | null;
  primary_clinic: string | null;
  emergency_hospital: string | null;
  master_notes: string | null;
  updated_at: string | null;
};

type MedicalAssignment = {
  id: string;
  resident_id: string;
  medical_provider_name: string;
  active: boolean;
  notes: string | null;
};

type VitalRecord = {
  id: string;
  resident_id: string | null;
  recorded_at: string;
  bp_systolic: number | null;
  bp_diastolic: number | null;
  pulse: number | null;
  temperature: number | null;
  spo2: number | null;
  reporter_name: string | null;
  content: string | null;
};

type ResidentSnapshot = {
  residents: ResidentMaster[];
  medicalAssignments: MedicalAssignment[];
  vitalLatest: VitalRecord[];
};

type EmergencyProfileRecord = {
  resident_id: string;
  profile_data: Partial<EmergencyDraft>;
  completed_at: string | null;
  review_due_date: string | null;
  updated_by: string | null;
  version: number;
  updated_at: string;
};

type EmergencyIncidentRecord = {
  id: string;
  resident_id: string;
  occurred_at: string;
  incident_data: Partial<EmergencyDraft>;
  created_by: string | null;
  created_at: string;
  updated_at: string;
};

type EmergencyRevision = {
  id: string;
  version: number;
  completed_at: string | null;
  review_due_date: string | null;
  changed_by: string | null;
  changed_at: string;
};

type EmergencyBundle = {
  profile: EmergencyProfileRecord | null;
  incidents: EmergencyIncidentRecord[];
  revisions: EmergencyRevision[];
};

type EmergencyDraft = {
  recordDate: string;
  updateDate: string;
  residentKana: string;
  residentName: string;
  gender: string;
  birthDate: string;
  residentAge: string;
  residentAddress: string;
  residentTel: string;
  emergency1Name: string;
  emergency1Relation: string;
  emergency1Tel: string;
  emergency1Address: string;
  emergency2Name: string;
  emergency2Relation: string;
  emergency2Tel: string;
  emergency2Address: string;
  requestSituation: string;
  fall: boolean;
  pain: boolean;
  painPart: string;
  paralysis: boolean;
  paralysisPart: string;
  vomiting: boolean;
  choking: boolean;
  treatment: string;
  requestTime: string;
  jcs: string;
  bpSystolic: string;
  bpDiastolic: string;
  respiration: string;
  pulse: string;
  temperature: string;
  spo2: string;
  lastMealTime: string;
  lastKnownWellTime: string;
  staffNameRole: string;
  staffContact: string;
  contactedMedical: '未選択' | 'あり' | 'なし';
  contactedMedicalName: string;
  highBloodPressure: boolean;
  usualBloodPressure: string;
  diabetes: boolean;
  insulinUse: string;
  fastingBloodSugar: string;
  respiratoryDisease: boolean;
  respiratoryDiseaseName: string;
  oxygen: string;
  cardiovascularDisease: boolean;
  cardiovascularDiseaseName: string;
  cerebrovascularDisease: boolean;
  cerebrovascularDiseaseName: string;
  cerebrovascularSequelae: string;
  cancer: boolean;
  cancerSite: string;
  dementia: boolean;
  dementiaLevel: string;
  communication: string;
  mentalDisease: boolean;
  mentalDiseaseName: string;
  otherDisease: boolean;
  otherDiseaseName: string;
  allergy: '未選択' | 'なし' | 'あり';
  allergen: string;
  medications: string;
  provider1Name: string;
  provider1Doctor: string;
  provider1Tel: string;
  provider1Department: string;
  provider2Name: string;
  provider2Doctor: string;
  provider2Tel: string;
  provider2Department: string;
  careLevel: string;
  adlMove: string;
  adlMeal: string;
  adlToilet: string;
  adlBath: string;
  consentDate: string;
  consenterName: string;
  ambulancePassenger: string;
};

const INCIDENT_KEYS: Array<keyof EmergencyDraft> = [
  'requestSituation',
  'fall',
  'pain',
  'painPart',
  'paralysis',
  'paralysisPart',
  'vomiting',
  'choking',
  'treatment',
  'requestTime',
  'jcs',
  'bpSystolic',
  'bpDiastolic',
  'respiration',
  'pulse',
  'temperature',
  'spo2',
  'lastMealTime',
  'lastKnownWellTime',
  'staffNameRole',
  'staffContact',
  'contactedMedical',
  'contactedMedicalName'
];

const incidentKeySet = new Set<keyof EmergencyDraft>(INCIDENT_KEYS);

const pickEmergencyProfile = (draft: EmergencyDraft): Partial<EmergencyDraft> =>
  Object.fromEntries(
    Object.entries(draft).filter(([key]) => !incidentKeySet.has(key as keyof EmergencyDraft))
  ) as Partial<EmergencyDraft>;

const pickEmergencyIncident = (draft: EmergencyDraft): Partial<EmergencyDraft> =>
  Object.fromEntries(
    Object.entries(draft).filter(([key]) => incidentKeySet.has(key as keyof EmergencyDraft))
  ) as Partial<EmergencyDraft>;

type TextFieldProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  placeholder?: string;
};

const SESSION_KEY = 'satsuki.residents.session.v1';

const FACILITY = {
  name: 'さつきホーム',
  address: '〒361-0011 埼玉県行田市荒木2131-3',
  tel: '048-550-7633'
};

const todayInput = (): string => {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60 * 1000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
};

const toDateInput = (value: string | null | undefined): string => value ? value.slice(0, 10) : '';

const normalizeText = (value: string | null | undefined): string =>
  (value ?? '')
    .normalize('NFKC')
    .replace(/[ 　\t\r\n]/g, '')
    .replace(/髙/g, '高')
    .replace(/﨑/g, '崎')
    .replace(/惠/g, '恵')
    .toLowerCase();

const getResidentAge = (resident: ResidentMaster): string => {
  if (!resident.birth_date) {
    return resident.age_at_import ? String(resident.age_at_import) : '';
  }
  const birth = new Date(`${resident.birth_date.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(birth.getTime())) return resident.age_at_import ? String(resident.age_at_import) : '';
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age -= 1;
  }
  return String(age);
};

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const toReiwaParts = (value: string): { year: string; month: string; day: string } => {
  if (!value) return { year: '', month: '', day: '' };
  const [year, month, day] = value.split('-').map(part => Number(part));
  if (!year || !month || !day) return { year: '', month: '', day: '' };
  return {
    year: String(year - 2018),
    month: String(month),
    day: String(day)
  };
};

const toJapaneseEraParts = (value: string): { era: string; year: string; month: string; day: string } => {
  if (!value) return { era: '', year: '', month: '', day: '' };
  const [year, month, day] = value.split('-').map(part => Number(part));
  if (!year || !month || !day) return { era: '', year: '', month: '', day: '' };
  const dateValue = year * 10000 + month * 100 + day;
  if (dateValue >= 20190501) return { era: 'R', year: String(year - 2018), month: String(month), day: String(day) };
  if (dateValue >= 19890108) return { era: 'H', year: String(year - 1988), month: String(month), day: String(day) };
  if (dateValue >= 19261225) return { era: 'S', year: String(year - 1925), month: String(month), day: String(day) };
  if (dateValue >= 19120730) return { era: 'T', year: String(year - 1911), month: String(month), day: String(day) };
  return { era: '', year: String(year), month: String(month), day: String(day) };
};

const normalizeGender = (value: string | null | undefined): string => {
  const text = value ?? '';
  if (text.includes('男')) return '男';
  if (text.includes('女')) return '女';
  return '';
};

const vitalToDraft = (vital: VitalRecord | undefined) => ({
  bpSystolic: vital?.bp_systolic ? String(vital.bp_systolic) : '',
  bpDiastolic: vital?.bp_diastolic ? String(vital.bp_diastolic) : '',
  pulse: vital?.pulse ? String(vital.pulse) : '',
  temperature: vital?.temperature ? String(vital.temperature) : '',
  spo2: vital?.spo2 ? String(vital.spo2) : ''
});

const selectedProviders = (resident: ResidentMaster | null, assignments: MedicalAssignment[]): string[] => {
  const names = [
    resident?.primary_clinic ?? '',
    resident?.emergency_hospital ?? '',
    ...assignments.filter(item => item.active).map(item => item.medical_provider_name)
  ].map(value => value.trim()).filter(Boolean);
  return Array.from(new Set(names));
};

const buildDraft = (resident: ResidentMaster | null, assignments: MedicalAssignment[], vital: VitalRecord | undefined): EmergencyDraft => {
  const today = todayInput();
  const providers = selectedProviders(resident, assignments);
  const vitalDraft = vitalToDraft(vital);
  return {
    recordDate: today,
    updateDate: today,
    residentKana: resident?.kana ?? '',
    residentName: resident?.name ?? '',
    gender: normalizeGender(resident?.gender),
    birthDate: toDateInput(resident?.birth_date),
    residentAge: resident ? getResidentAge(resident) : '',
    residentAddress: '',
    residentTel: '',
    emergency1Name: '',
    emergency1Relation: '',
    emergency1Tel: '',
    emergency1Address: '',
    emergency2Name: '',
    emergency2Relation: '',
    emergency2Tel: '',
    emergency2Address: '',
    requestSituation: '',
    fall: false,
    pain: false,
    painPart: '',
    paralysis: false,
    paralysisPart: '',
    vomiting: false,
    choking: false,
    treatment: '',
    requestTime: '',
    jcs: '',
    bpSystolic: vitalDraft.bpSystolic,
    bpDiastolic: vitalDraft.bpDiastolic,
    respiration: '',
    pulse: vitalDraft.pulse,
    temperature: vitalDraft.temperature,
    spo2: vitalDraft.spo2,
    lastMealTime: '',
    lastKnownWellTime: '',
    staffNameRole: '',
    staffContact: '',
    contactedMedical: '未選択',
    contactedMedicalName: providers[0] ?? '',
    highBloodPressure: false,
    usualBloodPressure: '',
    diabetes: false,
    insulinUse: '',
    fastingBloodSugar: '',
    respiratoryDisease: false,
    respiratoryDiseaseName: '',
    oxygen: '',
    cardiovascularDisease: false,
    cardiovascularDiseaseName: '',
    cerebrovascularDisease: false,
    cerebrovascularDiseaseName: '',
    cerebrovascularSequelae: '',
    cancer: false,
    cancerSite: '',
    dementia: false,
    dementiaLevel: '',
    communication: '',
    mentalDisease: false,
    mentalDiseaseName: '',
    otherDisease: false,
    otherDiseaseName: '',
    allergy: '未選択',
    allergen: '',
    medications: '',
    provider1Name: providers[0] ?? '',
    provider1Doctor: '',
    provider1Tel: '',
    provider1Department: '',
    provider2Name: providers[1] ?? '',
    provider2Doctor: '',
    provider2Tel: '',
    provider2Department: '',
    careLevel: resident?.care_level ?? '',
    adlMove: '',
    adlMeal: '',
    adlToilet: '',
    adlBath: '',
    consentDate: '',
    consenterName: '',
    ambulancePassenger: ''
  };
};

const mergeEmergencyProfile = (base: EmergencyDraft, profile: Partial<EmergencyDraft> | null | undefined): EmergencyDraft => ({
  ...base,
  ...(profile ?? {}),
  residentKana: base.residentKana,
  residentName: base.residentName,
  gender: base.gender,
  birthDate: base.birthDate,
  residentAge: base.residentAge,
  careLevel: base.careLevel,
  bpSystolic: base.bpSystolic,
  bpDiastolic: base.bpDiastolic,
  pulse: base.pulse,
  temperature: base.temperature,
  spo2: base.spo2
});

const clearEmergencyIncident = (draft: EmergencyDraft, base: EmergencyDraft): EmergencyDraft => ({
  ...draft,
  ...pickEmergencyIncident(base),
  recordDate: todayInput()
});

const emergencyOccurredAt = (draft: EmergencyDraft): string => {
  const date = draft.recordDate || todayInput();
  const time = draft.requestTime || '00:00';
  const occurredAt = new Date(`${date}T${time}:00`);
  return Number.isNaN(occurredAt.getTime()) ? new Date().toISOString() : occurredAt.toISOString();
};

const incidentDateParts = (occurredAt: string): { date: string; time: string } => {
  const value = new Date(occurredAt);
  if (Number.isNaN(value.getTime())) return { date: todayInput(), time: '' };
  const offset = value.getTimezoneOffset() * 60 * 1000;
  const local = new Date(value.getTime() - offset).toISOString();
  return { date: local.slice(0, 10), time: local.slice(11, 16) };
};

const EmergencyInfoPage = () => {
  const [searchParams] = useSearchParams();
  const [sessionToken, setSessionToken] = useState(() => {
    if (typeof window === 'undefined') return '';
    return window.sessionStorage.getItem(SESSION_KEY) ?? '';
  });
  const [passcode, setPasscode] = useState('');
  const [authError, setAuthError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [snapshot, setSnapshot] = useState<ResidentSnapshot>({ residents: [], medicalAssignments: [], vitalLatest: [] });
  const [selectedResidentId, setSelectedResidentId] = useState('');
  const [search, setSearch] = useState('');

  const residentById = useMemo(() => new Map(snapshot.residents.map(resident => [resident.id, resident])), [snapshot.residents]);
  const activeResidents = useMemo(
    () => snapshot.residents
      .filter(resident => resident.active && resident.status === 'active')
      .sort(compareResidentsByKana),
    [snapshot.residents]
  );
  const latestVitalByResident = useMemo(() => {
    const map = new Map<string, VitalRecord>();
    snapshot.vitalLatest.forEach(record => {
      if (record.resident_id) map.set(record.resident_id, record);
    });
    return map;
  }, [snapshot.vitalLatest]);
  const assignmentsByResident = useMemo(() => {
    const map = new Map<string, MedicalAssignment[]>();
    snapshot.medicalAssignments.forEach(assignment => {
      const rows = map.get(assignment.resident_id) ?? [];
      rows.push(assignment);
      map.set(assignment.resident_id, rows);
    });
    return map;
  }, [snapshot.medicalAssignments]);
  const visibleResidents = useMemo(() => {
    const query = normalizeText(search);
    return activeResidents.filter(resident => {
      if (!query) return true;
      const providerText = (assignmentsByResident.get(resident.id) ?? []).map(item => item.medical_provider_name).join(' ');
      return normalizeText(`${resident.name} ${resident.kana ?? ''} ${resident.room ?? ''} ${resident.care_level ?? ''} ${providerText}`).includes(query);
    });
  }, [activeResidents, assignmentsByResident, search]);
  const selectedResident = residentById.get(selectedResidentId) ?? visibleResidents[0] ?? activeResidents[0] ?? null;
  const selectedAssignments = useMemo(
    () => selectedResident ? assignmentsByResident.get(selectedResident.id) ?? [] : [],
    [assignmentsByResident, selectedResident]
  );
  const selectedVital = selectedResident ? latestVitalByResident.get(selectedResident.id) : undefined;
  const [draft, setDraft] = useState<EmergencyDraft>(() => buildDraft(null, [], undefined));
  const [profile, setProfile] = useState<EmergencyProfileRecord | null>(null);
  const [incidents, setIncidents] = useState<EmergencyIncidentRecord[]>([]);
  const [revisions, setRevisions] = useState<EmergencyRevision[]>([]);
  const [selectedIncidentId, setSelectedIncidentId] = useState('');
  const [profileCompleted, setProfileCompleted] = useState(false);
  const [reviewDueDate, setReviewDueDate] = useState('');
  const [profileDirty, setProfileDirty] = useState(false);
  const [incidentDirty, setIncidentDirty] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const dirty = profileDirty || incidentDirty;
  const authenticated = Boolean(sessionToken);

  const loadSnapshot = async (token = sessionToken) => {
    if (!supabaseClient || !token) return;
    setLoading(true);
    try {
      const { data, error } = await supabaseClient.rpc('resident_get_snapshot', { _session_token: token });
      if (error) throw error;
      const nextSnapshot = data as ResidentSnapshot;
      setSnapshot({
        residents: nextSnapshot.residents ?? [],
        medicalAssignments: nextSnapshot.medicalAssignments ?? [],
        vitalLatest: nextSnapshot.vitalLatest ?? []
      });
      const requestedResidentId = searchParams.get('resident') ?? '';
      const requestedExists = nextSnapshot.residents?.some(resident => resident.id === requestedResidentId);
      setSelectedResidentId(prev => (requestedExists ? requestedResidentId : prev) || nextSnapshot.residents?.find(resident => resident.status === 'active')?.id || nextSnapshot.residents?.[0]?.id || '');
      setAuthError(null);
    } catch (error: any) {
      const message = error?.message ?? String(error);
      setAuthError(message);
      if (message.includes('認証') || error?.code === '28000') {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (event: FormEvent) => {
    event.preventDefault();
    if (!supabaseClient) {
      setAuthError('Supabaseの設定が見つかりません。');
      return;
    }
    setLoading(true);
    setAuthError(null);
    try {
      const { data, error } = await supabaseClient.rpc('medicine_start_session', { _passcode: passcode });
      if (error) throw error;
      const token = (data as { token: string }).token;
      setSessionToken(token);
      window.sessionStorage.setItem(SESSION_KEY, token);
      setPasscode('');
      await loadSnapshot(token);
    } catch (error: any) {
      setAuthError(error?.message ?? String(error));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setSessionToken('');
    window.sessionStorage.removeItem(SESSION_KEY);
    setSnapshot({ residents: [], medicalAssignments: [], vitalLatest: [] });
    setSelectedResidentId('');
  };

  useEffect(() => {
    if (sessionToken) {
      void loadSnapshot(sessionToken);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionToken]);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = '救急情報提供書 | さつきホーム';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  useEffect(() => {
    const base = buildDraft(selectedResident, selectedAssignments, selectedVital);
    setDraft(base);
    setProfile(null);
    setIncidents([]);
    setRevisions([]);
    setSelectedIncidentId('');
    setProfileCompleted(false);
    setReviewDueDate('');
    setProfileDirty(false);
    setIncidentDirty(false);
    setSaveError(null);

    const client = supabaseClient;
    if (!selectedResident || !sessionToken || !client) return;

    let cancelled = false;
    const loadEmergency = async () => {
      const { data, error } = await client.rpc('resident_get_emergency_bundle', {
        _session_token: sessionToken,
        _resident_id: selectedResident.id
      });
      if (cancelled) return;
      if (error) {
        setSaveError(`救急情報の取得に失敗しました: ${error.message}`);
        return;
      }
      const bundle = data as EmergencyBundle;
      setProfile(bundle.profile ?? null);
      setIncidents(bundle.incidents ?? []);
      setRevisions(bundle.revisions ?? []);
      setProfileCompleted(Boolean(bundle.profile?.completed_at));
      setReviewDueDate(bundle.profile?.review_due_date?.slice(0, 10) ?? '');
      setDraft(mergeEmergencyProfile(base, bundle.profile?.profile_data));
    };
    void loadEmergency();
    return () => {
      cancelled = true;
    };
  }, [selectedResident, selectedAssignments, selectedVital, sessionToken]);

  const updateDraft = <Key extends keyof EmergencyDraft>(key: Key, value: EmergencyDraft[Key]) => {
    setDraft(prev => ({ ...prev, [key]: value }));
    if (incidentKeySet.has(key) || key === 'recordDate') {
      setIncidentDirty(true);
    } else {
      setProfileDirty(true);
    }
    setSaveStatus('idle');
  };

  const saveEmergencyProfile = async () => {
    if (!supabaseClient || !selectedResident) return;
    setSaveStatus('saving');
    setSaveError(null);
    const { data, error } = await supabaseClient.rpc('resident_save_emergency_profile', {
      _session_token: sessionToken,
      _resident_id: selectedResident.id,
      _profile_data: pickEmergencyProfile(draft),
      _completed: profileCompleted,
      _review_due_date: reviewDueDate || null,
      _updated_by: '管理画面'
    });
    if (error) {
      setSaveStatus('error');
      setSaveError(`基本情報を保存できませんでした: ${error.message}`);
      return;
    }
    const saved = data as EmergencyProfileRecord;
    setProfile(saved);
    setRevisions(prev => [{
      id: `${saved.resident_id}-${saved.version}`,
      version: saved.version,
      completed_at: saved.completed_at,
      review_due_date: saved.review_due_date,
      changed_by: saved.updated_by,
      changed_at: saved.updated_at
    }, ...prev.filter(item => item.version !== saved.version)].slice(0, 20));
    setProfileDirty(false);
    setSaveStatus('saved');
  };

  const saveEmergencyIncident = async () => {
    if (!supabaseClient || !selectedResident) return;
    setSaveStatus('saving');
    setSaveError(null);
    const { data, error } = await supabaseClient.rpc('resident_save_emergency_incident', {
      _session_token: sessionToken,
      _resident_id: selectedResident.id,
      _incident: {
        id: selectedIncidentId || null,
        occurred_at: emergencyOccurredAt(draft),
        incident_data: pickEmergencyIncident(draft),
        created_by: draft.staffNameRole || '管理画面'
      }
    });
    if (error) {
      setSaveStatus('error');
      setSaveError(`救急要請記録を保存できませんでした: ${error.message}`);
      return;
    }
    const saved = data as EmergencyIncidentRecord;
    setSelectedIncidentId(saved.id);
    setIncidents(prev => [saved, ...prev.filter(item => item.id !== saved.id)]
      .sort((a, b) => b.occurred_at.localeCompare(a.occurred_at)));
    setIncidentDirty(false);
    setSaveStatus('saved');
  };

  const selectEmergencyIncident = (incident: EmergencyIncidentRecord) => {
    if (incidentDirty && !window.confirm('未保存の救急要請記録があります。履歴を開いてよろしいですか？')) return;
    const base = mergeEmergencyProfile(buildDraft(selectedResident, selectedAssignments, selectedVital), pickEmergencyProfile(draft));
    const parts = incidentDateParts(incident.occurred_at);
    setDraft({
      ...base,
      ...incident.incident_data,
      recordDate: parts.date,
      requestTime: incident.incident_data.requestTime || parts.time
    });
    setSelectedIncidentId(incident.id);
    setIncidentDirty(false);
    setSaveStatus('idle');
    setSaveError(null);
  };

  const startNewEmergencyIncident = () => {
    if (incidentDirty && !window.confirm('未保存の救急要請記録があります。新規記録に切り替えてよろしいですか？')) return;
    const base = mergeEmergencyProfile(buildDraft(selectedResident, selectedAssignments, selectedVital), pickEmergencyProfile(draft));
    setDraft(clearEmergencyIncident(base, base));
    setSelectedIncidentId('');
    setIncidentDirty(false);
    setSaveStatus('idle');
    setSaveError(null);
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-slate-100 px-4 py-10 text-slate-900">
        <div className="mx-auto max-w-md rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-6 flex items-center gap-3">
            <div className="grid h-11 w-11 place-items-center rounded-lg bg-rose-700 text-white">
              <Ambulance className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-xl font-bold">救急情報提供書</h1>
              <p className="text-sm text-slate-500">さつきホーム</p>
            </div>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <input type="text" name="username" autoComplete="username" value="satsuki-emergency-admin" readOnly className="hidden" tabIndex={-1} />
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-slate-700">管理パスコード</span>
              <input
                id="emergency-passcode"
                name="emergency-passcode"
                type="password"
                autoComplete="current-password"
                value={passcode}
                onChange={event => setPasscode(event.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-rose-700"
                autoFocus
              />
            </label>
            {authError && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{authError}</div>}
            <button
              type="submit"
              disabled={loading}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-rose-700 px-4 py-2 font-medium text-white hover:bg-rose-800 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              <KeyRound className="h-4 w-4" />
              開く
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900 emergency-page-shell">
      <header className="border-b border-slate-200 bg-white emergency-screen-only">
        <div className="mx-auto flex max-w-[1600px] flex-wrap items-center justify-between gap-3 px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <div className="grid h-11 w-11 place-items-center rounded-lg bg-rose-700 text-white">
              <Ambulance className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-xl font-bold">救急情報提供書</h1>
              <p className="text-sm text-slate-500">救急要請時ToDo / 表面 / 裏面</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link to="/care" className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium hover:bg-slate-50">
              <ArrowLeft className="h-4 w-4" />
              ケアトップ
            </Link>
            <Link to={`/admin/residents${selectedResident ? `?resident=${selectedResident.id}&detail=emergency` : ''}`} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium hover:bg-slate-50">
              <UsersRound className="h-4 w-4" />
              入居者管理
            </Link>
            <button onClick={() => loadSnapshot()} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium hover:bg-slate-50">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              更新
            </button>
            <button onClick={() => window.print()} className="inline-flex items-center gap-2 rounded-lg bg-rose-700 px-3 py-2 text-sm font-medium text-white hover:bg-rose-800">
              <Printer className="h-4 w-4" />
              印刷
            </button>
            <button onClick={handleLogout} className="inline-flex items-center gap-2 rounded-lg bg-slate-800 px-3 py-2 text-sm font-medium text-white hover:bg-slate-900">
              <LogOut className="h-4 w-4" />
              ロック
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto grid max-w-[1600px] gap-4 px-4 py-4 lg:grid-cols-[360px_minmax(0,1fr)] sm:px-6 emergency-screen-only">
        {authError && (
          <div className="lg:col-span-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {authError}
          </div>
        )}

        <aside className="space-y-3">
          <div className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="relative mb-3">
              <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <input
                value={search}
                onChange={event => setSearch(event.target.value)}
                placeholder="入居者検索"
                className="w-full rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm outline-none focus:border-rose-700"
              />
            </div>
            <div className="max-h-[calc(100vh-210px)] space-y-1 overflow-auto pr-1">
              {visibleResidents.map((resident, index) => {
                const vital = latestVitalByResident.get(resident.id);
                return (
                  <button
                    key={resident.id}
                    onClick={() => setSelectedResidentId(resident.id)}
                    className={`w-full rounded-lg border px-3 py-2 text-left transition ${
                      selectedResident?.id === resident.id
                        ? 'border-rose-300 bg-rose-50'
                        : 'border-transparent hover:border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex min-w-0 items-start gap-2">
                        <span className="mt-0.5 grid h-6 w-6 flex-none place-items-center rounded-md bg-slate-100 text-[11px] font-bold text-slate-600">{index + 1}</span>
                        <div className="min-w-0">
                        <div className="truncate font-semibold">{resident.name}</div>
                        <div className={`mt-0.5 truncate text-xs ${hasResidentKana(resident) ? 'text-slate-400' : 'font-semibold text-amber-700'}`}>
                          {resident.kana || 'ふりがな未登録'}
                        </div>
                        <div className="mt-1 flex flex-wrap gap-x-2 gap-y-1 text-xs text-slate-500">
                          <span>{resident.room ? `居室 ${resident.room}` : '居室未設定'}</span>
                          <span>{resident.care_level || '介護度未設定'}</span>
                        </div>
                        </div>
                      </div>
                      <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200">入居中</span>
                    </div>
                    <div className="mt-2 flex items-center gap-1 text-xs text-slate-500">
                      <HeartPulse className="h-3.5 w-3.5" />
                      {vital ? `${formatDateTime(vital.recorded_at)} ${vital.bp_systolic && vital.bp_diastolic ? `${vital.bp_systolic}/${vital.bp_diastolic}` : ''}` : 'バイタル未登録'}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </aside>

        <section className="min-w-0 space-y-4">
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold">{selectedResident?.name ?? '入居者未選択'}</h2>
                <p className="text-sm text-slate-500">
                  {selectedResident ? `${selectedResident.kana || 'かな未設定'} / ${selectedResident.care_level || '介護度未設定'} / ${selectedResident.gender || '性別未設定'}` : '-'}
                </p>
              </div>
              <div className="rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-600">
                施設: {FACILITY.name} / {FACILITY.tel}
              </div>
            </div>

            <div className="mb-5 grid gap-3 xl:grid-cols-[minmax(0,1.35fr)_minmax(320px,0.65fr)]">
              <div className="rounded-lg border border-rose-200 bg-rose-50/60 p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h3 className="font-semibold text-slate-900">救急情報の保存</h3>
                    <p className="mt-1 text-xs text-slate-600">基本情報と、救急要請ごとの記録を分けて保存します。</p>
                  </div>
                  <div className="text-xs font-medium">
                    {saveStatus === 'saving' && <span className="text-slate-600">保存中...</span>}
                    {saveStatus === 'saved' && !dirty && <span className="text-emerald-700">保存しました</span>}
                    {profileDirty && saveStatus !== 'saving' && <span className="mr-2 text-amber-700">基本情報が未保存</span>}
                    {incidentDirty && saveStatus !== 'saving' && <span className="text-rose-700">救急要請記録が未保存</span>}
                  </div>
                </div>
                {saveError && <div className="mt-3 rounded-lg border border-red-200 bg-white px-3 py-2 text-sm text-red-700">{saveError}</div>}
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <label className="flex min-h-[42px] items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium">
                    <input
                      type="checkbox"
                      checked={profileCompleted}
                      onChange={event => {
                        setProfileCompleted(event.target.checked);
                        setProfileDirty(true);
                        setSaveStatus('idle');
                      }}
                      className="h-4 w-4 accent-rose-700"
                    />
                    基本情報の入力完了
                  </label>
                  <TextField
                    label="次回見直し日"
                    type="date"
                    value={reviewDueDate}
                    onChange={value => {
                      setReviewDueDate(value);
                      setProfileDirty(true);
                      setSaveStatus('idle');
                    }}
                  />
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={saveEmergencyProfile}
                    disabled={!selectedResident || saveStatus === 'saving'}
                    className="inline-flex items-center gap-2 rounded-lg bg-slate-800 px-3 py-2 text-sm font-medium text-white hover:bg-slate-900 disabled:bg-slate-300"
                  >
                    <Save className="h-4 w-4" />
                    基本情報を保存
                  </button>
                  <button
                    type="button"
                    onClick={saveEmergencyIncident}
                    disabled={!selectedResident || saveStatus === 'saving'}
                    className="inline-flex items-center gap-2 rounded-lg bg-rose-700 px-3 py-2 text-sm font-medium text-white hover:bg-rose-800 disabled:bg-slate-300"
                  >
                    <Ambulance className="h-4 w-4" />
                    {selectedIncidentId ? '救急要請記録を更新' : '救急要請記録を保存'}
                  </button>
                </div>
                <div className="mt-3 text-xs text-slate-500">
                  {profile ? `基本情報 v${profile.version} / 最終更新 ${formatDateTime(profile.updated_at)}` : '基本情報はまだ保存されていません'}
                  {revisions.length > 0 && ` / 更新履歴 ${revisions.length}件`}
                </div>
              </div>

              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="flex items-center gap-2 font-semibold"><History className="h-4 w-4 text-rose-700" />救急要請履歴</h3>
                  <button type="button" onClick={startNewEmergencyIncident} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium hover:bg-slate-50">新規記録</button>
                </div>
                <div className="mt-3 max-h-40 space-y-1 overflow-auto pr-1">
                  {incidents.length === 0 && <p className="rounded-lg bg-slate-50 px-3 py-3 text-sm text-slate-500">保存済みの救急要請記録はありません。</p>}
                  {incidents.map(incident => (
                    <button
                      type="button"
                      key={incident.id}
                      onClick={() => selectEmergencyIncident(incident)}
                      className={`w-full rounded-lg border px-3 py-2 text-left text-sm ${selectedIncidentId === incident.id ? 'border-rose-300 bg-rose-50' : 'border-transparent bg-slate-50 hover:border-slate-200'}`}
                    >
                      <span className="block font-medium">{formatDateTime(incident.occurred_at)}</span>
                      <span className="mt-0.5 block truncate text-xs text-slate-500">{incident.incident_data.requestSituation || '状況未入力'}{incident.created_by ? ` / ${incident.created_by}` : ''}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid gap-5 xl:grid-cols-2">
              <SectionPanel title="入居者基本情報">
                <div className="grid gap-3 md:grid-cols-2">
                  <TextField label="記載日" type="date" value={draft.recordDate} onChange={value => updateDraft('recordDate', value)} />
                  <TextField label="更新日" type="date" value={draft.updateDate} onChange={value => updateDraft('updateDate', value)} />
                  <TextField label="フリガナ" value={draft.residentKana} onChange={value => updateDraft('residentKana', value)} />
                  <TextField label="氏名" value={draft.residentName} onChange={value => updateDraft('residentName', value)} />
                  <SelectField label="性別" value={draft.gender} options={['', '男', '女']} onChange={value => updateDraft('gender', value)} />
                  <TextField label="生年月日" type="date" value={draft.birthDate} onChange={value => updateDraft('birthDate', value)} />
                  <TextField label="年齢" value={draft.residentAge} onChange={value => updateDraft('residentAge', value)} />
                  <TextField label="本人TEL" value={draft.residentTel} onChange={value => updateDraft('residentTel', value)} />
                </div>
                <TextAreaField label="入居者住所" value={draft.residentAddress} rows={2} onChange={value => updateDraft('residentAddress', value)} />
              </SectionPanel>

              <SectionPanel title="緊急連絡先">
                <div className="grid gap-3 md:grid-cols-2">
                  <TextField label="1 氏名" value={draft.emergency1Name} onChange={value => updateDraft('emergency1Name', value)} />
                  <TextField label="1 続柄" value={draft.emergency1Relation} onChange={value => updateDraft('emergency1Relation', value)} />
                  <TextField label="1 TEL" value={draft.emergency1Tel} onChange={value => updateDraft('emergency1Tel', value)} />
                  <TextField label="2 氏名" value={draft.emergency2Name} onChange={value => updateDraft('emergency2Name', value)} />
                  <TextField label="2 続柄" value={draft.emergency2Relation} onChange={value => updateDraft('emergency2Relation', value)} />
                  <TextField label="2 TEL" value={draft.emergency2Tel} onChange={value => updateDraft('emergency2Tel', value)} />
                </div>
                <TextAreaField label="1 住所" value={draft.emergency1Address} rows={2} onChange={value => updateDraft('emergency1Address', value)} />
                <TextAreaField label="2 住所" value={draft.emergency2Address} rows={2} onChange={value => updateDraft('emergency2Address', value)} />
              </SectionPanel>

              <SectionPanel title="救急要請時情報">
                <TextAreaField label="救急要請に至った状況" value={draft.requestSituation} rows={4} onChange={value => updateDraft('requestSituation', value)} />
                <div className="grid gap-3 md:grid-cols-2">
                  <CheckText label="転倒" checked={draft.fall} onChange={value => updateDraft('fall', value)} />
                  <CheckText label="嘔吐" checked={draft.vomiting} onChange={value => updateDraft('vomiting', value)} />
                  <CheckWithInput label="痛み" checked={draft.pain} value={draft.painPart} onCheckedChange={value => updateDraft('pain', value)} onValueChange={value => updateDraft('painPart', value)} />
                  <CheckWithInput label="麻痺" checked={draft.paralysis} value={draft.paralysisPart} onCheckedChange={value => updateDraft('paralysis', value)} onValueChange={value => updateDraft('paralysisPart', value)} />
                  <CheckText label="窒息" checked={draft.choking} onChange={value => updateDraft('choking', value)} />
                  <TextField label="実施した処置・薬剤" value={draft.treatment} onChange={value => updateDraft('treatment', value)} />
                </div>
                <div className="grid gap-3 md:grid-cols-3">
                  <TextField label="記録時刻" type="time" value={draft.requestTime} onChange={value => updateDraft('requestTime', value)} />
                  <TextField label="意識 JCS" value={draft.jcs} onChange={value => updateDraft('jcs', value)} />
                  <TextField label="呼吸数" value={draft.respiration} onChange={value => updateDraft('respiration', value)} />
                  <TextField label="脈拍" value={draft.pulse} onChange={value => updateDraft('pulse', value)} />
                  <TextField label="体温" value={draft.temperature} onChange={value => updateDraft('temperature', value)} />
                  <TextField label="SpO2" value={draft.spo2} onChange={value => updateDraft('spo2', value)} />
                  <TextField label="血圧 上" value={draft.bpSystolic} onChange={value => updateDraft('bpSystolic', value)} />
                  <TextField label="血圧 下" value={draft.bpDiastolic} onChange={value => updateDraft('bpDiastolic', value)} />
                  <TextField label="最終食事" type="time" value={draft.lastMealTime} onChange={value => updateDraft('lastMealTime', value)} />
                  <TextField label="最終健在時間" type="time" value={draft.lastKnownWellTime} onChange={value => updateDraft('lastKnownWellTime', value)} />
                  <TextField label="担当職員 氏名（職種）" value={draft.staffNameRole} onChange={value => updateDraft('staffNameRole', value)} />
                  <TextField label="担当職員 連絡先" value={draft.staffContact} onChange={value => updateDraft('staffContact', value)} />
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  <SelectField label="連絡済医療機関" value={draft.contactedMedical} options={['未選択', 'あり', 'なし']} onChange={value => updateDraft('contactedMedical', value as EmergencyDraft['contactedMedical'])} />
                  <TextField label="医療機関名" value={draft.contactedMedicalName} onChange={value => updateDraft('contactedMedicalName', value)} />
                </div>
              </SectionPanel>

              <SectionPanel title="疾患・医療機関・ADL">
                <div className="grid gap-3 md:grid-cols-2">
                  <CheckWithInput label="高血圧 普段の血圧" checked={draft.highBloodPressure} value={draft.usualBloodPressure} onCheckedChange={value => updateDraft('highBloodPressure', value)} onValueChange={value => updateDraft('usualBloodPressure', value)} />
                  <CheckWithInput label="糖尿病 空腹時血糖値" checked={draft.diabetes} value={draft.fastingBloodSugar} onCheckedChange={value => updateDraft('diabetes', value)} onValueChange={value => updateDraft('fastingBloodSugar', value)} />
                  <TextField label="インスリン使用" value={draft.insulinUse} onChange={value => updateDraft('insulinUse', value)} />
                  <CheckWithInput label="呼吸器疾患 病名" checked={draft.respiratoryDisease} value={draft.respiratoryDiseaseName} onCheckedChange={value => updateDraft('respiratoryDisease', value)} onValueChange={value => updateDraft('respiratoryDiseaseName', value)} />
                  <TextField label="在宅酸素" value={draft.oxygen} onChange={value => updateDraft('oxygen', value)} />
                  <CheckWithInput label="循環器疾患 病名" checked={draft.cardiovascularDisease} value={draft.cardiovascularDiseaseName} onCheckedChange={value => updateDraft('cardiovascularDisease', value)} onValueChange={value => updateDraft('cardiovascularDiseaseName', value)} />
                  <CheckWithInput label="脳疾患 病名" checked={draft.cerebrovascularDisease} value={draft.cerebrovascularDiseaseName} onCheckedChange={value => updateDraft('cerebrovascularDisease', value)} onValueChange={value => updateDraft('cerebrovascularDiseaseName', value)} />
                  <TextField label="脳疾患 後遺症" value={draft.cerebrovascularSequelae} onChange={value => updateDraft('cerebrovascularSequelae', value)} />
                  <CheckWithInput label="悪性腫瘍 部位" checked={draft.cancer} value={draft.cancerSite} onCheckedChange={value => updateDraft('cancer', value)} onValueChange={value => updateDraft('cancerSite', value)} />
                  <CheckWithInput label="認知症 程度" checked={draft.dementia} value={draft.dementiaLevel} onCheckedChange={value => updateDraft('dementia', value)} onValueChange={value => updateDraft('dementiaLevel', value)} />
                  <TextField label="意思疎通" value={draft.communication} onChange={value => updateDraft('communication', value)} />
                  <CheckWithInput label="精神疾患 病名" checked={draft.mentalDisease} value={draft.mentalDiseaseName} onCheckedChange={value => updateDraft('mentalDisease', value)} onValueChange={value => updateDraft('mentalDiseaseName', value)} />
                  <CheckWithInput label="その他 病名等" checked={draft.otherDisease} value={draft.otherDiseaseName} onCheckedChange={value => updateDraft('otherDisease', value)} onValueChange={value => updateDraft('otherDiseaseName', value)} />
                  <SelectField label="アレルギー" value={draft.allergy} options={['未選択', 'なし', 'あり']} onChange={value => updateDraft('allergy', value as EmergencyDraft['allergy'])} />
                  <TextField label="アレルゲン" value={draft.allergen} onChange={value => updateDraft('allergen', value)} />
                  <TextField label="医療機関1" value={draft.provider1Name} onChange={value => updateDraft('provider1Name', value)} />
                  <TextField label="医療機関2" value={draft.provider2Name} onChange={value => updateDraft('provider2Name', value)} />
                  <TextField label="介護区分" value={draft.careLevel} onChange={value => updateDraft('careLevel', value)} />
                  <TextField label="同意者氏名（続柄）" value={draft.consenterName} onChange={value => updateDraft('consenterName', value)} />
                </div>
                <TextAreaField label="内服薬情報" value={draft.medications} rows={3} onChange={value => updateDraft('medications', value)} />
              </SectionPanel>
            </div>
          </div>

          <EmergencyPrintView draft={draft} />
        </section>
      </main>

      <EmergencyPrintView draft={draft} printOnly />
      <EmergencyPrintStyles />
    </div>
  );
};

const SectionPanel = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
    <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
    {children}
  </section>
);

const TextField = ({ label, value, onChange, type = 'text', placeholder }: TextFieldProps) => (
  <label className="block">
    <span className="mb-1 block text-xs font-medium text-slate-600">{label}</span>
    <input
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={event => onChange(event.target.value)}
      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-rose-700"
    />
  </label>
);

const TextAreaField = ({ label, value, rows, onChange }: { label: string; value: string; rows: number; onChange: (value: string) => void }) => (
  <label className="block">
    <span className="mb-1 block text-xs font-medium text-slate-600">{label}</span>
    <textarea
      value={value}
      rows={rows}
      onChange={event => onChange(event.target.value)}
      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-rose-700"
    />
  </label>
);

const SelectField = ({ label, value, options, onChange }: { label: string; value: string; options: string[]; onChange: (value: string) => void }) => (
  <label className="block">
    <span className="mb-1 block text-xs font-medium text-slate-600">{label}</span>
    <select
      value={value}
      onChange={event => onChange(event.target.value)}
      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-rose-700"
    >
      {options.map(option => <option key={option} value={option}>{option || '未選択'}</option>)}
    </select>
  </label>
);

const CheckText = ({ label, checked, onChange }: { label: string; checked: boolean; onChange: (value: boolean) => void }) => (
  <label className="flex min-h-[38px] items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm">
    <input type="checkbox" checked={checked} onChange={event => onChange(event.target.checked)} className="h-4 w-4 accent-rose-700" />
    {label}
  </label>
);

const CheckWithInput = ({
  label,
  checked,
  value,
  onCheckedChange,
  onValueChange
}: {
  label: string;
  checked: boolean;
  value: string;
  onCheckedChange: (value: boolean) => void;
  onValueChange: (value: string) => void;
}) => (
  <label className="block">
    <span className="mb-1 flex items-center gap-2 text-xs font-medium text-slate-600">
      <input type="checkbox" checked={checked} onChange={event => onCheckedChange(event.target.checked)} className="h-4 w-4 accent-rose-700" />
      {label}
    </span>
    <input value={value} onChange={event => onValueChange(event.target.value)} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-rose-700" />
  </label>
);

const EmergencyPrintView = ({ draft, printOnly = false }: { draft: EmergencyDraft; printOnly?: boolean }) => (
  <div className={`emergency-print-root ${printOnly ? 'emergency-print-only' : 'emergency-preview-root'}`}>
    <ChecklistPage draft={draft} />
    <EmergencyFrontPage draft={draft} />
    <EmergencyBackPage draft={draft} />
  </div>
);

const Checkbox = ({ checked = false }: { checked?: boolean }) => <span className="print-checkbox">{checked ? '☑' : '☐'}</span>;
const PrintValue = ({ value }: { value: string }) => <span className="print-value">{value || '\u00a0'}</span>;
const PrintMultiline = ({ value, minHeight = '22mm' }: { value: string; minHeight?: string }) => <div className="print-multiline" style={{ minHeight }}>{value || '\u00a0'}</div>;

const ReiwaDate = ({ value }: { value: string }) => {
  const parts = toReiwaParts(value);
  return (
    <span className="reiwa-date">令和 <b>{parts.year || '\u00a0'}</b> 年 <b>{parts.month || '\u00a0'}</b> 月 <b>{parts.day || '\u00a0'}</b> 日</span>
  );
};

const JapaneseBirthDate = ({ value, age }: { value: string; age: string }) => {
  const parts = toJapaneseEraParts(value);
  return (
    <span className="birth-date">
      {parts.era || 'T S H R'} <b>{parts.year || '\u00a0'}</b> 年 <b>{parts.month || '\u00a0'}</b> 月 <b>{parts.day || '\u00a0'}</b> 日（<b>{age || '\u00a0'}</b>歳）
    </span>
  );
};

const ChecklistPage = ({ draft }: { draft: EmergencyDraft }) => (
  <section className="emergency-page emergency-checklist-page">
    <div className="checklist-box">
      <h2>― 救急要請時のToDoチェックリスト ―</h2>
      <h3>【平時からの準備】</h3>
      <ul>
        <li>□「救急情報提供書」の記載内容を定期的に確認・更新する。</li>
        <li>□ 施設訪問医及び協力医療機関と情報共有し、緊急時の連絡先や連絡可能時間を把握する。</li>
      </ul>
      <h3>【救急対応手順】</h3>
      <ul>
        <li>□ 救急対応手順（フロー図）により対応を確認。</li>
      </ul>
      <div className="checklist-indent">
        <p><b>①「施設訪問医及び協力医療機関医師等の指示どおり対応」の場合</b></p>
        <p>医療機関の受診が必要な場合は、施設訪問医及び協力医療機関医師等と相談の上、可能な範囲で独自の搬送手段（施設が所有する車両、患者等搬送事業者（民間救急や介護タクシー等））により医療機関に搬送することを検討する。</p>
        <p>また、家族が施設に来て、病院へ連れていくことが可能な場合は、上記の搬送手段や家族の自家用車での病院受診を検討する。</p>
        <p><b>②「119番通報（救急要請）」の場合</b></p>
        <p>施設訪問医及び協力医療機関医師等と相談の上、可能な範囲で搬送先医療機関を確保してから119番通報することが望ましい。</p>
        <p><b>③施設訪問医及び協力医療機関医師等と連絡を取れない場合</b></p>
        <p>♯7119（明らかに緊急性ありと判断される場合は119番通報（救急要請））</p>
      </div>
      <ul>
        <li>□ 施設責任者等へ緊急連絡を実施。</li>
        <li>□ 入所者家族に連絡（状況報告、搬送先医療機関への来院依頼、治療に係る意思確認等）⇒連絡した旨を救急情報提供書（表面）に記録</li>
        <li>□ 救急情報提供書（表面）の「救急要請時情報」を記入</li>
      </ul>
      <h3>【119番通報した場合の持ち物】</h3>
      <div className="checklist-grid">
        <span>□ 資格確認証</span><span>□ マイナンバーカード</span><span>□ 診察券</span>
        <span>□ 現金</span><span>□ 救急情報提供書（表・裏両面）</span><span></span>
        <span>□ 介護記録・看護記録</span><span>□ 診療情報提供書（紹介状）</span><span></span>
        <span>□ お薬手帳</span><span>□ 各種手帳（ペースメーカー手帳等）</span><span></span>
        <span>□ 処方されている薬</span><span>□ その他（　　　　　　　　　　　）</span><span></span>
      </div>
      <h3>【救急車の同乗者】<small>※病院で今回の病状や普段の様子を説明できる方</small></h3>
      <p className="passenger-line">
        <Checkbox checked={draft.ambulancePassenger === '医師'} /> 医師
        <Checkbox checked={draft.ambulancePassenger === '看護職員'} /> 看護職員
        <Checkbox checked={draft.ambulancePassenger === '介護職員'} /> 介護職員
        <Checkbox checked={Boolean(draft.ambulancePassenger && !['医師', '看護職員', '介護職員'].includes(draft.ambulancePassenger))} /> その他（<PrintValue value={!['医師', '看護職員', '介護職員'].includes(draft.ambulancePassenger) ? draft.ambulancePassenger : ''} />）
      </p>
      <p className="checklist-note">※ このチェックリストは、救急要請時に必要となる可能性が高い手順や持ち物を記載していますが、あくまでも参考として作成したものです。傷病者の状態によっては、準備ができなかったり、対応が変わってしまうことがあると思いますが、その時は傷病者への対応を優先してください。</p>
    </div>
    <PageNumber value="7" />
  </section>
);

const EmergencyFrontPage = ({ draft }: { draft: EmergencyDraft }) => (
  <section className="emergency-page">
    <div className="emergency-form-frame">
      <table className="emergency-table front-table">
        <tbody>
          <tr>
            <td rowSpan={3} className="form-title-cell">救急情報提供書</td>
            <th className="facility-label">施　設　名</th>
            <td><PrintValue value={FACILITY.name} /></td>
          </tr>
          <tr><th className="facility-label">住　　　所</th><td><PrintValue value={FACILITY.address} /></td></tr>
          <tr><th className="facility-label">Ｔ　Ｅ　Ｌ</th><td><PrintValue value={FACILITY.tel} /></td></tr>
        </tbody>
      </table>
      <div className="yellow-note">
        <p>※ この情報は、緊急時に救急隊がスムーズな病院選定や医師への引継ぎをするために使用するものです。なお、使用後の「救急情報提供書」は救急隊が処分いたします。</p>
        <p>※ 記入していただいた内容は救急業務以外には使用いたしません。</p>
        <p>※ 情報提供書の情報は状況が変わった際に適宜更新し、「更新年月日」も更新してください。</p>
      </div>
      <div className="section-band pink">※　入　居　者　基　本　情　報　①（事前に記載してください）</div>
      <table className="emergency-table compact-table">
        <tbody>
          <tr>
            <th className="side-label narrow">記載年月日</th><td><ReiwaDate value={draft.recordDate} /></td>
            <th className="side-label narrow">更新年月日</th><td><ReiwaDate value={draft.updateDate} /></td>
          </tr>
          <tr>
            <th rowSpan={3} className="green-side">入　居　者</th>
            <th className="thin-label">フリガナ</th><td><PrintValue value={draft.residentKana} /></td>
            <th className="thin-label">性　別</th><td>男 ・ 女　<PrintValue value={draft.gender} /></td>
            <th className="thin-label">生年月日</th><td><JapaneseBirthDate value={draft.birthDate} age={draft.residentAge} /></td>
          </tr>
          <tr>
            <th className="thin-label">氏　名</th><td><PrintValue value={draft.residentName} /></td>
            <td colSpan={4}></td>
          </tr>
          <tr>
            <th className="thin-label">住　所</th><td colSpan={3}><PrintValue value={draft.residentAddress} /></td>
            <th className="thin-label">TEL</th><td><PrintValue value={draft.residentTel} /></td>
          </tr>
          <EmergencyContactRow index="①" name={draft.emergency1Name} relation={draft.emergency1Relation} tel={draft.emergency1Tel} address={draft.emergency1Address} />
          <EmergencyContactRow index="②" name={draft.emergency2Name} relation={draft.emergency2Relation} tel={draft.emergency2Tel} address={draft.emergency2Address} />
        </tbody>
      </table>
      <div className="section-band yellow">※救急要請時情報（救急要請時に記載してください）</div>
      <div className="section-subtitle">救急要請に至った状況 今回はどうしましたか（可能な限り記載してください）</div>
      <PrintMultiline value={draft.requestSituation} minHeight="36mm" />
      <div className="symptom-row">
        転倒（<Checkbox checked={draft.fall} />有・<Checkbox />無）、
        痛み（<Checkbox checked={draft.pain} />有：部位名 <PrintValue value={draft.painPart} />　<Checkbox />無）、
        麻痺（<Checkbox checked={draft.paralysis} />有：部位名 <PrintValue value={draft.paralysisPart} />　<Checkbox />無）<br />
        嘔吐（<Checkbox checked={draft.vomiting} />有・<Checkbox />無）、
        窒息（<Checkbox checked={draft.choking} />有・<Checkbox />無）、
        実施した処置・薬剤（<PrintValue value={draft.treatment} />）
      </div>
      <div className="section-subtitle">直近のバイタルサイン等（施設側記載）【 <PrintValue value={draft.requestTime} /> 】</div>
      <table className="emergency-table vital-table">
        <tbody>
          <tr><th>意識</th><td>JCS <PrintValue value={draft.jcs} /></td><th>血圧</th><td><PrintValue value={draft.bpSystolic} /> / <PrintValue value={draft.bpDiastolic} /> mmHg</td></tr>
          <tr><th>呼吸数</th><td><PrintValue value={draft.respiration} /> 回/分　脈拍数 <PrintValue value={draft.pulse} /> 回/分</td><th>体温</th><td><PrintValue value={draft.temperature} /> ℃　SpO2 <PrintValue value={draft.spo2} /> %</td></tr>
          <tr><th>最終食事</th><td><PrintValue value={draft.lastMealTime} /></td><th>最終健在時間</th><td><PrintValue value={draft.lastKnownWellTime} /></td></tr>
          <tr><th>担当職員</th><td>氏名（職種）：<PrintValue value={draft.staffNameRole} /></td><th>連絡先</th><td><PrintValue value={draft.staffContact} /></td></tr>
          <tr><th>連絡済医療機関の有無</th><td><Checkbox checked={draft.contactedMedical === 'あり'} />あり ・ <Checkbox checked={draft.contactedMedical === 'なし'} />なし</td><th>医療機関名</th><td><PrintValue value={draft.contactedMedicalName} /></td></tr>
        </tbody>
      </table>
      <div className="rescue-title">救急隊記入欄</div>
      <PrintMultiline value="" minHeight="42mm" />
      <div className="section-subtitle">バイタルサイン（救急隊記載）【　　　時　　　分　】</div>
      <table className="emergency-table vital-table rescue-vital">
        <tbody>
          <tr><th>意識</th><td>JCS</td><th>呼吸数</th><td>回/分　脈拍数　回/分</td></tr>
          <tr><th>体温</th><td>℃　SpO2　%</td><th>血圧</th><td>／　mmHg</td></tr>
        </tbody>
      </table>
    </div>
    <PageNumber value="8" />
  </section>
);

const EmergencyContactRow = ({ index, name, relation, tel, address }: { index: string; name: string; relation: string; tel: string; address: string }) => (
  <>
    <tr>
      <th rowSpan={2} className="green-side">{index}<br />緊急連絡先</th>
      <th className="thin-label">氏　名</th><td><PrintValue value={name} /></td>
      <th className="thin-label">続　柄</th><td><PrintValue value={relation} /></td>
      <th className="thin-label">TEL</th><td><PrintValue value={tel} /></td>
    </tr>
    <tr>
      <th className="thin-label">住　所</th><td colSpan={4}><PrintValue value={address} /></td>
      <td className="pink-mini">※緊急連絡　済・未</td>
    </tr>
  </>
);

const EmergencyBackPage = ({ draft }: { draft: EmergencyDraft }) => (
  <section className="emergency-page">
    <div className="emergency-form-frame back-frame">
      <div className="section-band pink">※　入　居　者　基　本　情　報　②（事前に記載してください）</div>
      <table className="emergency-table back-table">
        <tbody>
          <tr><th><Checkbox checked={draft.highBloodPressure} />高 血 圧</th><td>普段の血圧　<PrintValue value={draft.usualBloodPressure} /> /　　　　mmHg</td></tr>
          <tr><th><Checkbox checked={draft.diabetes} />糖 尿 病</th><td>インスリン使用：<PrintValue value={draft.insulinUse} />　空腹時の血糖値：<PrintValue value={draft.fastingBloodSugar} /> mg/dL</td></tr>
          <tr><th><Checkbox checked={draft.respiratoryDisease} />呼吸器疾患</th><td>病名：<PrintValue value={draft.respiratoryDiseaseName} />　在宅酸素：<PrintValue value={draft.oxygen} /> L</td></tr>
          <tr><th><Checkbox checked={draft.cardiovascularDisease} />循環器疾患</th><td>病名：<PrintValue value={draft.cardiovascularDiseaseName} /></td></tr>
          <tr><th><Checkbox checked={draft.cerebrovascularDisease} />脳 疾 患</th><td>病名：<PrintValue value={draft.cerebrovascularDiseaseName} />　後遺症：<PrintValue value={draft.cerebrovascularSequelae || '失語症・右片麻痺・左片麻痺・全麻痺'} /></td></tr>
          <tr><th><Checkbox checked={draft.cancer} />悪性腫瘍</th><td>部位：<PrintValue value={draft.cancerSite} /></td></tr>
          <tr><th rowSpan={2}><Checkbox checked={draft.dementia} />認 知 症</th><td>程度：<PrintValue value={draft.dementiaLevel} />　□ ほぼ自立　□ 日常生活に支障　□ 常に要介護　他（　　　　）</td></tr>
          <tr><td>意思疎通：<PrintValue value={draft.communication} />　□ 可能　□ 困難（具体的な様子：　　　　　　　　　　　　　　　）</td></tr>
          <tr><th><Checkbox checked={draft.mentalDisease} />精神疾患</th><td>病名：<PrintValue value={draft.mentalDiseaseName} /></td></tr>
          <tr><th><Checkbox checked={draft.otherDisease} />そ の 他</th><td>病名等：<PrintValue value={draft.otherDiseaseName} /></td></tr>
          <tr><th><Checkbox checked={draft.allergy === 'あり'} />アレルギー</th><td><Checkbox checked={draft.allergy === 'なし'} />なし　<Checkbox checked={draft.allergy === 'あり'} />あり（アレルゲン：<PrintValue value={draft.allergen} />）</td></tr>
        </tbody>
      </table>
      <div className="section-band green">内服薬情報　（※ おくすり手帳や薬の成分表での代用可。内服している薬も御用意ください。）</div>
      <PrintMultiline value={draft.medications} minHeight="20mm" />
      <div className="section-band green">掛かりつけ又は協力医療機関等</div>
      <table className="emergency-table provider-table">
        <tbody>
          <tr><th rowSpan={2}>①</th><th>医療機関名</th><td><PrintValue value={draft.provider1Name} /></td><th>主治医氏名</th><td><PrintValue value={draft.provider1Doctor} /></td></tr>
          <tr><th>TEL</th><td><PrintValue value={draft.provider1Tel} /></td><th>診療科目</th><td><PrintValue value={draft.provider1Department} /></td></tr>
          <tr><th rowSpan={2}>②</th><th>医療機関名</th><td><PrintValue value={draft.provider2Name} /></td><th>主治医氏名</th><td><PrintValue value={draft.provider2Doctor} /></td></tr>
          <tr><th>TEL</th><td><PrintValue value={draft.provider2Tel} /></td><th>診療科目</th><td><PrintValue value={draft.provider2Department} /></td></tr>
        </tbody>
      </table>
      <div className="section-band green">要介護度、ADL等</div>
      <table className="emergency-table adl-table">
        <tbody>
          <tr><th>介護区分</th><td>要支援　1 ・ 2　　　要介護　1 ・ 2 ・ 3 ・ 4 ・ 5　　<PrintValue value={draft.careLevel} /></td></tr>
          <tr><th rowSpan={4}>ADL</th><td>移動　自立 ・ 一部介助 ・ 全介助　<PrintValue value={draft.adlMove} /></td></tr>
          <tr><td>食事　自立 ・ 一部介助 ・ 全介助　<PrintValue value={draft.adlMeal} /></td></tr>
          <tr><td>排泄　自立 ・ 一部介助 ・ 全介助　<PrintValue value={draft.adlToilet} /></td></tr>
          <tr><td>入浴　自立 ・ 一部介助 ・ 全介助　<PrintValue value={draft.adlBath} /></td></tr>
        </tbody>
      </table>
      <div className="section-band green">持ち物</div>
      <p className="red-belongings">お薬手帳、内服薬、マイナ保険証、資格確認証、診察券及び看護サマリーなどを御用意ください。</p>
      <div className="back-notes">
        <p>※ 家族等が病院に来られなかったり、救急車に同乗できない場合には、原則施設職員の同乗をお願いします。</p>
        <p>※ 救急車を要請する際は、可能な限り、協力医療機関等に連絡し、収容医療機関を確保してください。</p>
        <p>※ このシートの代用となるものがあれば、その写しを御提供いただいても結構です。</p>
        <p>※ 延命処置及びDNARに関する書類があれば、別途御用意ください。</p>
        <p>※ 記入は可能な範囲で結構です。生命に危険があると判断した場合は、救急情報提供書の記入よりも救命処置を優先してください。</p>
      </div>
      <div className="section-band pink">同　意　欄</div>
      <p className="consent-text">救急情報提供書に記載する個人情報について、緊急時に消防機関及び医療機関へ提供することに同意します。</p>
      <p className="consent-text">同意年月日　<ReiwaDate value={draft.consentDate} /></p>
      <p className="consent-text">同意者氏名（続柄）　<PrintValue value={draft.consenterName} /></p>
    </div>
    <PageNumber value="9" />
  </section>
);

const PageNumber = ({ value }: { value: string }) => <div className="page-number">{value}</div>;

const EmergencyPrintStyles = () => (
  <style>{`
    .emergency-print-only { display: none; }
    .emergency-preview-root { display: grid; gap: 18px; }
    .emergency-page {
      box-sizing: border-box;
      width: 210mm;
      min-height: 297mm;
      margin: 0 auto;
      background: #fff;
      color: #111;
      font-family: "Hiragino Kaku Gothic ProN", "Yu Gothic", "Meiryo", sans-serif;
      padding: 10mm 14mm 8mm;
      box-shadow: 0 18px 50px rgba(15, 23, 42, 0.16);
      position: relative;
      page-break-after: always;
      break-after: page;
    }
    .emergency-page:last-child { page-break-after: auto; break-after: auto; }
    .page-number {
      position: absolute;
      bottom: 3.5mm;
      left: 0;
      right: 0;
      text-align: center;
      font-size: 14pt;
      line-height: 1;
    }
    .checklist-box {
      border: 1.4px solid #333;
      min-height: 276mm;
      padding: 10mm 11mm 7mm;
      font-size: 12.2pt;
      line-height: 1.42;
    }
    .checklist-box h2 {
      margin: 0 0 12mm;
      text-align: center;
      font-size: 20pt;
      font-weight: 700;
    }
    .checklist-box h3 {
      margin: 7mm 0 3mm;
      font-size: 14.5pt;
      font-weight: 700;
    }
    .checklist-box ul {
      margin: 0 0 3mm;
      padding: 0 0 0 8mm;
      list-style: none;
    }
    .checklist-box li { margin: 1.8mm 0; }
    .checklist-indent { margin-left: 10mm; }
    .checklist-indent p { margin: 2mm 0; }
    .checklist-grid {
      display: grid;
      grid-template-columns: 1fr 1.35fr 1fr;
      gap: 4mm 10mm;
      margin-left: 8mm;
    }
    .passenger-line {
      display: flex;
      flex-wrap: wrap;
      gap: 4mm;
      align-items: center;
      margin: 4mm 0 0 8mm;
    }
    .checklist-note {
      margin-top: 12mm;
      font-size: 11.5pt;
    }
    .emergency-form-frame {
      border: 1.4px solid #222;
      min-height: 276mm;
      font-size: 9.6pt;
      line-height: 1.25;
    }
    .emergency-table {
      width: 100%;
      border-collapse: collapse;
      table-layout: fixed;
    }
    .emergency-table th,
    .emergency-table td {
      border: 1px solid #333;
      padding: 1.4mm 1.6mm;
      vertical-align: middle;
      word-break: break-word;
    }
    .front-table td,
    .front-table th { height: 8mm; }
    .form-title-cell {
      width: 48%;
      text-align: center;
      font-size: 18pt;
      font-weight: 700;
      letter-spacing: 0;
    }
    .facility-label {
      width: 28mm;
      text-align: center;
      font-weight: 700;
      letter-spacing: 0.28em;
    }
    .yellow-note {
      border-left: 1px solid #333;
      border-right: 1px solid #333;
      border-bottom: 1px solid #333;
      background: #fff200;
      padding: 1.5mm 2mm;
      font-size: 8.7pt;
      font-weight: 600;
    }
    .yellow-note p { margin: 0.8mm 0; }
    .section-band {
      border-left: 1px solid #333;
      border-right: 1px solid #333;
      border-bottom: 1px solid #333;
      padding: 1.5mm 2mm;
      text-align: center;
      font-size: 12pt;
      font-weight: 700;
      letter-spacing: 0.12em;
    }
    .section-band.pink { background: #f48ac3; }
    .section-band.yellow { background: #fff200; }
    .section-band.green { background: #c8ffd0; text-align: left; letter-spacing: 0; }
    .compact-table th,
    .compact-table td { height: 7.5mm; }
    .side-label,
    .thin-label {
      background: #fff;
      font-weight: 700;
      text-align: center;
    }
    .side-label.narrow { width: 22mm; }
    .green-side {
      width: 20mm;
      background: #c8ffd0;
      text-align: center;
      font-weight: 700;
    }
    .pink-mini {
      background: #f48ac3;
      text-align: center;
      font-weight: 700;
    }
    .section-subtitle {
      border-left: 1px solid #333;
      border-right: 1px solid #333;
      border-bottom: 1px solid #333;
      background: #fff200;
      padding: 1mm 1.6mm;
      text-align: center;
      font-weight: 700;
    }
    .print-multiline {
      border-left: 1px solid #333;
      border-right: 1px solid #333;
      border-bottom: 1px solid #333;
      padding: 1.8mm;
      white-space: pre-wrap;
      word-break: break-word;
    }
    .symptom-row {
      border-left: 1px solid #333;
      border-right: 1px solid #333;
      border-bottom: 1px solid #333;
      background: #fff200;
      padding: 1.2mm 1.6mm;
      font-size: 8.6pt;
      font-weight: 600;
    }
    .vital-table th {
      width: 24mm;
      background: #fff200;
      text-align: center;
      font-weight: 700;
    }
    .vital-table td { background: #fff200; }
    .rescue-title {
      border-left: 1px solid #333;
      border-right: 1px solid #333;
      border-bottom: 1px solid #333;
      padding: 1.3mm;
      text-align: center;
      font-size: 15pt;
      font-weight: 700;
    }
    .rescue-vital th,
    .rescue-vital td { background: #fff; }
    .back-frame { font-size: 9.4pt; }
    .back-table th {
      width: 29mm;
      text-align: left;
      font-weight: 700;
      white-space: nowrap;
    }
    .provider-table th { width: 23mm; }
    .provider-table th:first-child { width: 12mm; text-align: center; }
    .adl-table th { width: 28mm; text-align: center; }
    .red-belongings {
      margin: 0;
      border-left: 1px solid #333;
      border-right: 1px solid #333;
      border-bottom: 1px solid #333;
      padding: 1.7mm 2mm;
      color: #e60012;
      font-weight: 700;
    }
    .back-notes {
      border-left: 1px solid #333;
      border-right: 1px solid #333;
      border-bottom: 1px solid #333;
      padding: 1.7mm 2mm;
      font-size: 9pt;
    }
    .back-notes p,
    .consent-text { margin: 1mm 0; }
    .consent-text {
      border-left: 1px solid #333;
      border-right: 1px solid #333;
      border-bottom: 1px solid #333;
      padding: 1.7mm 2mm;
    }
    .print-value {
      display: inline-block;
      min-width: 18mm;
      border-bottom: 1px solid #222;
      padding: 0 1mm;
      white-space: pre-wrap;
    }
    .print-checkbox { margin: 0 0.7mm; font-weight: 700; }
    .reiwa-date { white-space: nowrap; }
    .reiwa-date b,
    .birth-date b {
      display: inline-block;
      min-width: 5mm;
      border-bottom: 1px solid #222;
      text-align: center;
      font-weight: 500;
    }
    .birth-date {
      display: inline-block;
      max-width: 100%;
      white-space: normal;
      font-size: 8.3pt;
      line-height: 1.2;
    }
    .birth-date b { min-width: 4mm; }
    @media print {
      @page { size: A4 portrait; margin: 0; }
      html, body { background: #fff !important; }
      body * { visibility: hidden !important; }
      .emergency-print-only,
      .emergency-print-only * { visibility: visible !important; }
      .emergency-print-only {
        display: block !important;
        position: absolute !important;
        inset: 0 !important;
        width: 210mm !important;
        background: #fff !important;
      }
      .emergency-screen-only,
      .emergency-preview-root { display: none !important; }
      .emergency-page {
        width: 210mm !important;
        height: 297mm !important;
        min-height: 0 !important;
        margin: 0 !important;
        padding: 8mm 10mm 7mm !important;
        box-shadow: none !important;
        overflow: hidden !important;
      }
      .checklist-box {
        min-height: 0 !important;
        height: 282mm !important;
        padding: 6mm 8mm 5mm !important;
        font-size: 10.8pt !important;
        line-height: 1.28 !important;
      }
      .checklist-box h2 {
        margin-bottom: 7mm !important;
        font-size: 18pt !important;
      }
      .checklist-box h3 { margin: 4.2mm 0 2mm !important; }
      .checklist-box li { margin: 1.1mm 0 !important; }
      .checklist-indent p { margin: 1.3mm 0 !important; }
      .checklist-grid { gap: 2.2mm 8mm !important; }
      .checklist-note { margin-top: 7mm !important; font-size: 10.3pt !important; }
      .emergency-form-frame {
        min-height: 0 !important;
        height: 282mm !important;
      }
      .page-number { bottom: 2mm !important; }
    }
  `}</style>
);

export default EmergencyInfoPage;
